import isNumber from "lodash-es/isNumber";
import { normalize } from "normalizr";
import { parse } from "query-string";
import React from "react";
import { all, call, put, select, take, takeEvery, takeLatest } from "redux-saga/effects";

import { groupPostCreateFormName } from "@/mk2/apps/blogs/components/BlogPostCreateForm";
import { BoxedHashtags } from "@/mk2/apps/blogs/components/HashtagsBox";
import {
    blogsShortMessageConvertSaveAsForumThread,
    blogsShortMessageConvertSaveAsPhotoblogShortMessage,
    blogsShortMessageConvertSaveBoth,
    blogsShortMessageConvertShowModal,
} from "@/mk2/apps/blogs/containers/BlogsShortMessageCreate/BlogsShortMessageCreate.actions";
import {
    CORE_TESTERS,
    createForumThread,
    generateDiscussionTitleAndPerex,
} from "@/mk2/apps/blogs/utils/discussionUtils";
import {
    BrandDaysCampaignEntity,
    BrandDaysCampaignSchema,
    PostEntity,
    PostSchema,
} from "@/mk2/apps/forum/schemas";
import { forumPostDetailUrl } from "@/mk2/apps/forum/urls";
import { parsePageNumber } from "@/mk2/apps/forum/utils";
import { groupsGroupUrl } from "@/mk2/apps/groups/urls";
import { ComplaintEntity, ComplaintSchema } from "@/mk2/containers/Complaint/Complaint.schemas";
import { toolbarCloseAll } from "@/mk2/containers/Toolbar/Toolbar.actions";
import {
    XHRAction,
    XHRFormError,
    XHRGetError,
    handleXHRGetErrorSaga,
    normalizeError,
} from "@/mk2/helpers/api";
import { handleXHRFormErrorSaga, handleXHRFormSuccessSaga } from "@/mk2/helpers/form";
import { PhotoUploadStatus } from "@/mk2/helpers/form.reducers";
import { scrollElementTo } from "@/mk2/helpers/scrolling";
import { tupdate } from "@/mk2/helpers/types";
import { removeEmpty, removePage1, url } from "@/mk2/helpers/urls";
import { getLogger } from "@/mk2/logger";
import { getRequestUser } from "@/mk2/reducers";
import {
    BlogPostEntity,
    BlogPostSchema,
    EshopProductEntity,
    EshopProductSchema,
    GroupEntity,
    GroupMemberEntity,
    GroupMemberSchema,
    GroupSchema,
    UserEntity,
    WikiArticleEntity,
    WikiArticleSchema,
} from "@/mk2/schemas";
import { HistoryAction, replaceInPWA } from "@/mk2/services/browserHistory";
import {
    blogs_api_get_group_feed_url,
    blogs_api_group_create_post_url,
    blogs_api_guide_send_heart_url,
    forum_api_post_create_url,
} from "@/mk/urls/functions";

import {
    GROUP_FEED_FETCH_TRIGGER,
    GROUP_FEED_SCROLLTO_TRIGGER,
    GROUP_GUIDE_COMPLETE_TRIGGER,
    GROUP_POST_CREATE_TRIGGER,
    GroupFeedFetchNormalizedResponse,
    GroupFeedFetchTriggerAction,
    GroupFeedScrollToTriggerAction,
    GroupGuideCompleteNormalizedResponse,
    GroupGuideCompleteTriggerAction,
    GroupPostCreateNormalizedResponse,
    GroupPostCreateTriggerAction,
    groupFeedFetchApi,
    groupFeedScrollToTrigger,
    groupGuideCompleteApi,
    groupPostCreateApi,
} from "./GroupFeed.actions";
import { ScrollToPosition } from "./GroupFeedPage";

const logger = getLogger("groups/GroupFeed.sagas");

interface ApiResponseGroupFeed {
    body: {
        group: GroupEntity;
        neighbourGroups: GroupEntity[];
        moderators: GroupMemberEntity[];
        posts: BlogPostEntity[];
        complaints: ComplaintEntity[];
        count: number;
        page: number;
        numberOfPages: number;
        deletedBy: UserEntity;
        memberships: number[];
        askedToJoin: number[];
        favorites: number[];
        isBlocked: boolean;
        isSubscribed: boolean;
        hasComplaints: boolean;
        hashtags: BoxedHashtags;
        activeBrandDays: { posts: PostEntity[]; campaign: BrandDaysCampaignEntity };
        countyId: number;
        productsCount: number;
        userComment: boolean;
        userPosts: boolean;
        isClosed: boolean;
        commentCount: number;

        eshopProductsHeader: EshopProductEntity[];
        eshopProducts: EshopProductEntity[];
        eshopProductsFooter: EshopProductEntity[];

        promoReviewArticlesHeader: WikiArticleEntity[];
        promoReviewArticlesMiddle: WikiArticleEntity[];
        promoReviewArticlesFooter: WikiArticleEntity[];
    };
}

function* fetchGroupFeed({
    groupId,
    numPostsBeforeLoad,
    pageRef,
    orderBy,
    query,
    xhr,
    location,
    routeNavigate,
}: GroupFeedFetchTriggerAction & XHRAction & HistoryAction) {
    const qs = parse(location.search);

    if (!pageRef) {
        // if pageRef is 0, negative or invalid string, we redirect us to page=1
        const gotoUrl = url(
            groupsGroupUrl,
            { groupId },
            removeEmpty(removePage1({ ...qs, page: 1 })),
        );
        replaceInPWA(routeNavigate, gotoUrl);
        return;
    } else if (pageRef && qs.page && qs.page !== pageRef.toString()) {
        // if we succeed parsing incorrect string (e.g. page=3a), then redir to correct page (e.g. page=3)
        const gotoUrl = url(
            groupsGroupUrl,
            { groupId },
            removeEmpty(removePage1({ ...qs, page: pageRef })),
        );
        replaceInPWA(routeNavigate, gotoUrl);
        return;
    }

    if (numPostsBeforeLoad > 0 && qs.scrollto) {
        // if data is already loaded, scroll to bottom immediately
        // so that user does not experience scroll delayed by api call
        yield put(groupFeedScrollToTrigger(qs.scrollto as ScrollToPosition));
    }

    yield put(groupFeedFetchApi.request(groupId, pageRef, orderBy, query));

    try {
        const response: ApiResponseGroupFeed = yield call(() =>
            xhr.get(blogs_api_get_group_feed_url(groupId), {
                orderby: orderBy,
                page: pageRef,
                query,

                // previous url (from standard window.document.referrer)
                pageRef: typeof window !== "undefined" ? window.document.referrer : "",

                // previous url (within pwa from react router)
                pwaRef: location.state?.UNSAFE_mkGoBackUrl || "",
            }),
        );

        // ak skusame nahravat zly page number (negativny, vacsi ako pocet stranok, atd.), backend nas redirectne
        if (isNumber(pageRef) && pageRef !== response.body.page) {
            throw new Error("Wrong page");
        }

        const normalizedResponse: GroupFeedFetchNormalizedResponse = normalize(
            {
                complaints: response.body.complaints,
                group: response.body.group,
                neighbourGroups: response.body.neighbourGroups,
                posts: response.body.posts,
                moderators: response.body.moderators,
                activeBrandDays: response.body.activeBrandDays,
                userComment: response.body.userComment,
                userPosts: response.body.userPosts,
                isClosed: response.body.isClosed,
                commentCount: response.body.commentCount,
                eshopProductsHeader: response.body.eshopProductsHeader,
                eshopProducts: response.body.eshopProducts,
                eshopProductsFooter: response.body.eshopProductsFooter,
                promoReviewArticlesHeader: response.body.promoReviewArticlesHeader,
                promoReviewArticlesMiddle: response.body.promoReviewArticlesMiddle,
                promoReviewArticlesFooter: response.body.promoReviewArticlesFooter,
            },
            {
                complaints: [ComplaintSchema],
                group: GroupSchema,
                neighbourGroups: [GroupSchema],
                posts: [BlogPostSchema],
                moderators: [GroupMemberSchema],
                eshopProductsHeader: [EshopProductSchema],
                eshopProducts: [EshopProductSchema],
                eshopProductsFooter: [EshopProductSchema],
                promoReviewArticlesHeader: [WikiArticleSchema],
                promoReviewArticlesMiddle: [WikiArticleSchema],
                promoReviewArticlesFooter: [WikiArticleSchema],
                activeBrandDays: { posts: [PostSchema], campaign: BrandDaysCampaignSchema },
            },
        );

        yield put(
            groupFeedFetchApi.success(
                groupId,
                pageRef,
                orderBy,
                query,
                response.body.page,
                response.body.numberOfPages,
                response.body.count,
                normalizedResponse,
                response.body.memberships,
                response.body.askedToJoin,
                response.body.favorites,
                response.body.isBlocked,
                response.body.isSubscribed,
                response.body.hasComplaints,
                response.body.hashtags,
                response.body.countyId,
                response.body.productsCount,
                response.body.userComment,
                response.body.userPosts,
                response.body.isClosed,
                response.body.commentCount,
            ),
        );

        if (pageRef === "last") {
            // replace page='last' with real page index
            const gotoUrl = url(
                groupsGroupUrl,
                { groupId },
                removeEmpty(removePage1({ ...qs, page: response.body.page })),
            );
            replaceInPWA(routeNavigate, gotoUrl);
        } else if (qs.page === "1") {
            // remove ?page=1 from url
            const gotoUrl = url(
                groupsGroupUrl,
                { groupId },
                removeEmpty(removePage1({ ...qs, page: 1 })),
            );
            replaceInPWA(routeNavigate, gotoUrl);
        }

        const numPostsAfterLoad = response.body.posts ? response.body.posts.length : 0;
        if (numPostsBeforeLoad !== numPostsAfterLoad && qs.scrollto) {
            // other count of posts, either first load, or data on server changed, so scroll again
            yield put(groupFeedScrollToTrigger(qs.scrollto as ScrollToPosition));
        }
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, location, routeNavigate, logger);
        yield put(
            groupFeedFetchApi.failure(groupId, pageRef, orderBy, query, normalizeError(error)),
        );
    }
}

function scrolltoGroupFeed({ scrollto }: GroupFeedScrollToTriggerAction) {
    if (scrollto === ScrollToPosition.LastPost) {
        scrollElementTo("#scrolltoLastHere", "window-bottom", 0);
    } else if (scrollto === ScrollToPosition.FirstPost) {
        scrollElementTo("#scrolltoFirstHere", "window-top", 40);
    }
}

export interface ApiResponsePostCreate {
    body: {
        post: BlogPostEntity;
    };
}

export interface ApiResponseForumPostCreate {
    body: {
        post: PostEntity;
        postIsRemovedFromFeeds: boolean;
        notificationsOn: number[];
    };
}

function* createGroupPost({
    groupId,
    postFd,
    xhr,
}: {
    groupId: number;
    postFd: any;
    xhr: any;
}): Generator<any, any, any> {
    const response: ApiResponsePostCreate = yield call(() =>
        xhr.post(blogs_api_group_create_post_url(groupId), {
            postData: tupdate(postFd, {
                photos: postFd.photos
                    ? postFd.photos.filter((photo) => photo.status === PhotoUploadStatus.SUCCESS)
                    : [],
            }),
        }),
    );

    return response;
}

function* postCreate({
    groupId,
    pageRef,
    postFd,
    xhr,
    location,
    routeNavigate,
}: GroupPostCreateTriggerAction & XHRAction & HistoryAction) {
    // Check if user is a core tester
    const currentUser = yield select((state: any) => getRequestUser(state));
    const isCoreTester = currentUser && CORE_TESTERS.includes(currentUser.username);

    const query = parse(location.search, { parseNumbers: true });
    const searchQuery = query.query;
    const currentPage = parsePageNumber(location);
    const formName = groupPostCreateFormName(groupId, pageRef);

    const perex = postFd.perex ?? "";
    const hasQuestion = perex.includes("?");

    // If feature flag is enabled and conditions are met, show modal
    // Only show for posts without photos (photo support not implemented yet)
    const hasPhotos = postFd.photos && postFd.photos.length > 0;

    if (isCoreTester && hasQuestion && !hasPhotos) {
        yield put(groupPostCreateApi.request(groupId, postFd));

        try {
            // Show modal
            yield put(blogsShortMessageConvertShowModal(groupId));

            // Wait for user choice
            const action = yield take([
                blogsShortMessageConvertSaveAsForumThread().type,
                blogsShortMessageConvertSaveAsPhotoblogShortMessage().type,
                blogsShortMessageConvertSaveBoth().type,
            ]);

            if (action.type === blogsShortMessageConvertSaveAsForumThread().type) {
                // Create only forum thread
                const response: ApiResponseForumPostCreate = yield call(createForumThread, {
                    postFd,
                    xhr,
                });

                const post = response.body.post;
                const gotoUrl = url(forumPostDetailUrl, {
                    topicSlug: post.topic.slug,
                    postSlug: post.slug,
                });
                yield handleXHRFormSuccessSaga(formName, routeNavigate, gotoUrl);
                return;
            }

            if (action.type === blogsShortMessageConvertSaveAsPhotoblogShortMessage().type) {
                // Fall through to create group post
            }

            if (action.type === blogsShortMessageConvertSaveBoth().type) {
                // Create both forum thread and group post
                try {
                    const forumResponse: ApiResponseForumPostCreate = yield call(
                        createForumThread,
                        { postFd, xhr },
                    );

                    const forumPost = forumResponse.body.post;

                    // Then create group post
                    const groupResponse = yield call(createGroupPost, { groupId, postFd, xhr });

                    // Redirect to forum thread
                    const gotoUrl = url(forumPostDetailUrl, {
                        topicSlug: forumPost.topic.slug,
                        postSlug: forumPost.slug,
                    });
                    yield handleXHRFormSuccessSaga(formName, routeNavigate, gotoUrl);

                    const normalizedResponse: GroupPostCreateNormalizedResponse = normalize(
                        groupResponse.body,
                        {
                            post: BlogPostSchema,
                        },
                    );

                    yield put(groupPostCreateApi.success(groupId, postFd, normalizedResponse));
                    return;
                } catch (err) {
                    yield handleXHRFormErrorSaga(formName, err as XHRFormError, logger);
                    yield put(groupPostCreateApi.failure(groupId, postFd, normalizeError(err)));
                    return;
                }
            }
        } catch (error) {
            yield handleXHRFormErrorSaga(formName, error as XHRFormError, logger);
            yield put(groupPostCreateApi.failure(groupId, postFd, normalizeError(error)));
            return;
        }
    }

    // Create group post (normal flow or after cancel)
    yield put(groupPostCreateApi.request(groupId, postFd));
    try {
        const response: ApiResponsePostCreate = yield call(createGroupPost, {
            groupId,
            postFd,
            xhr,
        });

        const normalizedResponse: GroupPostCreateNormalizedResponse = normalize(response.body, {
            post: BlogPostSchema,
        });
        // close toolbar, e.g. photos might be opened
        yield put(toolbarCloseAll(formName));
        const nextPage = 1;
        const gotoUrl =
            nextPage !== currentPage || searchQuery !== undefined
                ? url(
                      groupsGroupUrl,
                      { groupId },
                      removePage1(
                          tupdate(query as { [key: string]: number | string }, {
                              page: nextPage,
                              query: undefined,
                          }),
                      ),
                  )
                : // stay on the same url
                  null;
        yield handleXHRFormSuccessSaga(formName, routeNavigate, gotoUrl);
        yield put(groupPostCreateApi.success(groupId, postFd, normalizedResponse));
    } catch (error) {
        yield handleXHRFormErrorSaga(formName, error as XHRFormError, logger);
        yield put(groupPostCreateApi.failure(groupId, postFd, normalizeError(error)));
    }
}
export interface ApiResponsesendHearthCongrats {
    body: {
        isClosed: boolean;
    };
}
function* sendHearthCongrats({
    groupId,
    isClosedByUser,
    xhr,
    location,
    routeNavigate,
}: GroupGuideCompleteTriggerAction & XHRAction & HistoryAction) {
    yield put(groupGuideCompleteApi.request(groupId, isClosedByUser));
    try {
        const response: ApiResponsesendHearthCongrats = yield call(() =>
            xhr.get(blogs_api_guide_send_heart_url(), {
                groupId,
                isClosedByUser,
            }),
        );

        const normalizedResponse: GroupGuideCompleteNormalizedResponse = normalize(
            {
                isClosed: response.body.isClosed,
            },
            {},
        );
        yield put(
            groupGuideCompleteApi.success(
                groupId,
                isClosedByUser,
                normalizedResponse,
                response.body.isClosed,
            ),
        );
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, location, routeNavigate, logger);
        yield put(groupGuideCompleteApi.failure(groupId, isClosedByUser, normalizeError(error)));
    }
}

export default function* root() {
    yield all([
        // musi byt takeEvery pre GROUP_FEED_FETCH_TRIGGER a nie takeLatest,
        // aby sa docitali vsetky stranky, cez ktore rychlo pageujem. Inak sa killne
        // redux saga pocas jej vykonavania a loadingState ostane v stave LOADING.
        // Ked sa na nu znova vratim, uz sa nezacne znova nacitavat, lebo nie je v stave INIT
        takeEvery(GROUP_FEED_FETCH_TRIGGER, fetchGroupFeed),
        takeLatest(GROUP_FEED_SCROLLTO_TRIGGER, scrolltoGroupFeed),
        takeEvery(GROUP_POST_CREATE_TRIGGER, postCreate),
        takeEvery(GROUP_GUIDE_COMPLETE_TRIGGER, sendHearthCongrats),
    ]);
}
